import { useEffect, useLayoutEffect, useRef } from "react";
import {
  removeStylePropertyIfSet,
  scheduleResizeUpdate,
  setStylePropertyIfChanged,
} from "../utils/resizeObserverSafe";

const LEFT_ALIGN_SELECTORS = [
  ".home-page__feed-list",
  ".home-page__feed-head",
  ".poem-detail-page__layout",
];

function offsetWithinAncestor(child, ancestor) {
  if (!child || !ancestor) return 0;
  return child.getBoundingClientRect().top - ancestor.getBoundingClientRect().top;
}

function findLeftAlignTarget(root) {
  for (const selector of LEFT_ALIGN_SELECTORS) {
    const el = root.querySelector(selector);
    if (el) return el;
  }
  return null;
}

function getLeftSidebarOffset(layout, root) {
  const sidebarColumn = layout.querySelector(".app-layout-sidebar");
  if (!sidebarColumn) return null;

  // Scroll panes use shared --app-pane-top padding on each column.
  if (
    root.classList.contains("group-page") ||
    root.classList.contains("home-page--panes") ||
    root.classList.contains("poem-detail-page--panes")
  ) {
    return 0;
  }

  const sidebar = layout.querySelector(".app-sidebar");
  const main = layout.querySelector(".app-layout-main");
  const alignTarget = findLeftAlignTarget(root);
  if (!sidebar || !main || !alignTarget) return null;

  const mainTopInLayout = offsetWithinAncestor(main, layout);
  const targetTopInMain = offsetWithinAncestor(alignTarget, main);
  const sidebarTopInLayout = offsetWithinAncestor(sidebarColumn, layout);

  return Math.max(
    0,
    Math.round(mainTopInLayout + targetTopInMain - sidebarTopInLayout)
  );
}

/**
 * Aligns the left sidebar column with the main content on dual-sidebar pages.
 */
export function useFeedSidebarAlign(deps = []) {
  const rootRef = useRef(null);

  const updateLayout = () => {
    const root = rootRef.current;
    if (!root) return;

    const layout = root.closest(".app-layout");
    if (!layout) return;

    if (window.innerWidth < 960) {
      removeStylePropertyIfSet(layout, "--sidebar-align-offset");
      return;
    }

    const offset = getLeftSidebarOffset(layout, root);
    if (offset === null) {
      removeStylePropertyIfSet(layout, "--sidebar-align-offset");
      return;
    }

    setStylePropertyIfChanged(layout, "--sidebar-align-offset", `${offset}px`);
  };

  const scheduleLayoutUpdate = scheduleResizeUpdate(updateLayout);

  useLayoutEffect(() => {
    scheduleLayoutUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const layout = root.closest(".app-layout");
    if (!layout) return undefined;

    const observer = new ResizeObserver(scheduleLayoutUpdate);
    const alignTarget = findLeftAlignTarget(root);
    const feedBody = root.querySelector(".home-page__feed-body");
    const groupLayout = root.querySelector(".group-page__layout");
    const groupHero = root.querySelector(".group-page__hero");
    const poemLayout = root.querySelector(".poem-detail-page__layout");
    const sidebarColumn = layout.querySelector(".app-layout-sidebar");
    const main = layout.querySelector(".app-layout-main");

    if (alignTarget) observer.observe(alignTarget);
    if (feedBody) observer.observe(feedBody);
    if (groupLayout) observer.observe(groupLayout);
    if (groupHero) observer.observe(groupHero);
    if (poemLayout) observer.observe(poemLayout);
    if (sidebarColumn) observer.observe(sidebarColumn);
    if (main) observer.observe(main);

    window.addEventListener("resize", scheduleLayoutUpdate);

    return () => {
      scheduleLayoutUpdate.cancel?.();
      observer.disconnect();
      window.removeEventListener("resize", scheduleLayoutUpdate);
      if (root.classList.contains("group-page")) {
        layout.style.removeProperty("--sidebar-align-offset");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return rootRef;
}
